import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle, IonCheckbox,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonPage,
  IonText,
  IonToast,
} from "@ionic/react";
import React, {useEffect, useState} from "react";
import { useHistory } from "react-router-dom";
import { sha512 } from "js-sha512";
import { User } from "../../ebelediye/entities/User";
import { KullaniciTip } from "../../enums/KullaniciTip";
import { ROLE } from "../../enums/ROLE";
import { AuthContext } from "../../providers/authContext";
import userService from "../../services/UserService";
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import "./Login.css";
import {useDynamicManifest} from "../../hooks/useDynamicManifest";

const REMEMBER_KEY = 'yoneticiRememberMe';
const CRED_KEY = 'yoneticiSavedCredentials';

const YoneticiLogin: React.FC = () => {
  const { kurumBilgileri, logo, logIn } = React.useContext(AuthContext);
  const history = useHistory();
  const [username, setUsername] = useState<string>("");
  const [usernameMessage, setUsernameMessage] = useState<string>();
  const [password, setPassword] = useState<string>("");
  const [passwordMessage, setPasswordMessage] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showToastMessage, setShowToastMessage] = useState<string>();
  const [beniHatirla, setBeniHatirla] = useState(false);

  useDynamicManifest({
    name: "MobilSistem",
    shortName: "Yönetici",
    startUrl: "/yonetici"
  });

  useEffect(() => {
    (async () => {
      try {
        const { value } = await SecureStoragePlugin.get({ key: REMEMBER_KEY });
        if (value === 'true') {
          const { value: credJson } = await SecureStoragePlugin.get({ key: CRED_KEY });
          if (credJson) {
            const { kullaniciAdi: ku, sifre: sf } = JSON.parse(credJson);
            setUsername(decodeURIComponent(ku));
            setPassword(decodeURIComponent(sf));
            setBeniHatirla(true);
          }
        }
      } catch {
        // Anahtar yoksa hata fırlatır, normal karşılanabilir
      }
    })();
  }, []);

  const removeKeyIfExists = async (key: string) => {
    try {
      await SecureStoragePlugin.remove({ key });
    } catch (e) {
      // Key zaten yoksa bu normal, sessizce geç
    }
  };

  const handleLogin = () => {
    let errorFound = false;
    setUsernameMessage(undefined);
    setPasswordMessage(undefined);
    if (!username) {
      setUsernameMessage("* Lütfen Kullanıcı Adınızı Giriniz!");
      errorFound = true;
    }
    if (!password) {
      setPasswordMessage("* Lütfen Şifrenizi Giriniz!");
      errorFound = true;
    }
    if (errorFound) {
      return;
    }

    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur();

    setLoading(true);
    const user = new User(
      {
        sicilNo: -1,
        tip: KullaniciTip.YONETICI,
        adSoyad: undefined,
        menus: [],
      },
      ROLE.Yonetici,
      username!,
      sha512(password!)
    );
    userService
      .login(user)
      .then(async () => {
        if (beniHatirla) {
          await SecureStoragePlugin.set({ key: REMEMBER_KEY, value: 'true' });
          await SecureStoragePlugin.set({
            key: CRED_KEY,
            value: JSON.stringify({ kullaniciAdi: encodeURIComponent(username), sifre: encodeURIComponent(password) }),
          });
        } else {
          await removeKeyIfExists(REMEMBER_KEY);
          await removeKeyIfExists(CRED_KEY);
        }
        setUsername("");
        setPassword("");
        await logIn(user);
        history.push("/yonetici");
      })
      .catch((err) => {
        console.error(err.response);
        if (err.response?.status === 401) {
          setShowToastMessage("Kullanıcı Adı ve/veya Şifre Yanlış");
        } else {
          setShowToastMessage(err.message);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <IonPage>
      <IonContent fullscreen className="login-content">
        <div className="login-container">
          <IonLoading
            isOpen={loading}
            onDidDismiss={() => setLoading(false)}
            message={"Lütfen Bekleyin..."}
          />

          <IonToast
            isOpen={showToastMessage !== undefined}
            onDidDismiss={() => setShowToastMessage(undefined)}
            message={showToastMessage}
            duration={2000}
            position="top"
          />

          <IonCard className="login-card">
            <IonCardContent>
              <div className="login-header">
                {logo && (
                  <img
                    src={logo}
                    alt="Kurum Logo"
                    className="login-logo"
                  />
                )}

                <h1 className="login-title">Mobil Giriş</h1>

                <p className="login-subtitle">
                  {kurumBilgileri?.kisaTanim}
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
              >
                <IonItem className="input-item">
                  <IonLabel position="floating">
                    Kullanıcı Adı
                  </IonLabel>
                  <IonInput
                    name="usernameInput"
                    value={username}
                    onIonChange={(e) =>
                      setUsername(e.detail.value!)
                    }
                    clearInput
                  />
                </IonItem>

                {usernameMessage && (
                  <IonText color="danger">
                    <small>{usernameMessage}</small>
                  </IonText>
                )}

                <IonItem className="input-item">
                  <IonLabel position="floating">
                    Şifre
                  </IonLabel>
                  <IonInput
                    name="passwordInput"
                    value={password}
                    type="password"
                    onIonChange={(e) =>
                      setPassword(e.detail.value!)
                    }
                    clearInput
                  />
                </IonItem>

                {passwordMessage && (
                  <IonText color="danger">
                    <small>{passwordMessage}</small>
                  </IonText>
                )}

                <IonItem lines="none">
                  <IonCheckbox
                    slot="start"
                    checked={beniHatirla}
                    onIonChange={(e) => setBeniHatirla(e.detail.checked)}
                  />
                  <IonLabel>Beni Hatırla</IonLabel>
                </IonItem>

                <IonButton
                  expand="block"
                  type="submit"
                  className="login-button"
                >
                  Giriş Yap
                </IonButton>
              </form>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default YoneticiLogin;
